import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, CheckCircle2, FileText } from "lucide-react";
import { findParameterGaps, countGaps } from "@/lib/quoting/rfpParameterGaps";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { getPlanTypeMeta } from "@shared/schemas/planTypes";
import { HealthRFPForm } from "@/components/rfp/HealthRFPForm";
import { DentalRFPForm } from "@/components/rfp/DentalRFPForm";
import { VisionRFPForm } from "@/components/rfp/VisionRFPForm";
import { LifeRFPForm } from "@/components/rfp/LifeRFPForm";
import { DisabilityRFPForm } from "@/components/rfp/DisabilityRFPForm";
import { VoluntaryRFPForm } from "@/components/rfp/VoluntaryRFPForm";
import { useQuoteWizardContext } from "../QuoteWizardContext";

// "disability" and "voluntary" are RFP-form groupings, not plan types, so they
// have no TYPE_META entry — borrow the closest one for the icon and colour.
const META_TYPE_FOR_FORM: Record<string, string> = {
  disability: "ltd",
  voluntary: "accident",
};
const FORM_LABELS: Record<string, string> = {
  life: "Life & AD&D",
  disability: "Disability",
  voluntary: "Voluntary",
};

export function Step6RFPParameters() {
  const { quoteData, updateQuoteData } = useQuoteWizardContext();

  const needsRFP = quoteData.coverageTypes.filter((type) =>
    ["health", "dental", "vision", "life", "disability", "voluntary"].includes(type)
  );

  if (needsRFP.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">RFP Parameters</h3>
          <p className="text-muted-foreground">
            No RFP parameters needed for selected coverage types
          </p>
        </div>

        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h4 className="text-lg font-medium mb-2">No RFP Configuration Required</h4>
            <p className="text-muted-foreground">
              Your selected coverage types don't require RFP parameters. Click Next to
              continue.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // What each coverage is still missing. Nothing here is required — the same
  // check runs again on the send step, where it's the last chance to go back.
  // The parameters are the same either way — they still end up in front of
  // carriers — but through a GA the broker is asking their GA, so the wording
  // follows suit.
  const usingGa = (quoteData.rfpSendVia ?? "carriers") === "ga";

  const gaps = findParameterGaps(needsRFP, quoteData.rfpParameters ?? {});
  const gapCount = countGaps(gaps);
  const missingByType = new Map(gaps.map((g) => [g.coverageType, g.missing]));

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2">
          {usingGa ? "What are you asking your GA to quote?" : "What are you asking carriers to quote?"}
        </h3>
        <p className="text-muted-foreground">
          {usingGa
            ? "These answers go into the email your GA receives and passes to the carriers. The more you specify, the more comparable the proposals come back."
            : "These answers go into the email carriers receive. The more you specify, the more comparable the proposals come back."}
        </p>
      </div>

      {/* One tile per coverage, using the same icons and accent colours as the
          rest of the app (getTypeIcon / TYPE_META) so a dental form looks like
          dental everywhere. Clicking scrolls to that form — a stack of five long
          forms otherwise hides what's left to do. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {needsRFP.map((type) => {
          const missing = missingByType.get(type) ?? [];
          const metaType = META_TYPE_FOR_FORM[type] ?? type;
          const label = FORM_LABELS[type] ?? getPlanTypeMeta(metaType).label;
          return (
            <button
              key={type}
              type="button"
              onClick={() =>
                document.getElementById(`rfp-form-${type}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              className={`rounded-lg border p-3 text-left transition-colors hover:border-primary/50 ${
                missing.length ? "border-amber-300 dark:border-amber-800" : "border-emerald-300 dark:border-emerald-800"
              }`}
            >
              <CoverageIcon planType={metaType} className="mb-2" />
              <p className="text-xs font-semibold truncate">{label}</p>
              <p
                className={`text-[11px] flex items-center gap-1 ${
                  missing.length ? "text-amber-700 dark:text-amber-500" : "text-emerald-700 dark:text-emerald-500"
                }`}
              >
                {missing.length ? (
                  <>
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    {missing.length} to fill
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    Complete
                  </>
                )}
              </p>
            </button>
          );
        })}
      </div>

      {gapCount > 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Anything left blank is sent as "Not specified", and carriers quote to their own defaults.
        </p>
      )}

      {/* Each form used to carry an "Include current plan summary" tick that
          was stored and read by nothing. The send step does it for real. */}
      <p className="text-center text-xs text-muted-foreground">
        Plan summaries are attached on the last step, where you can see which ones are on file.
      </p>

      <div className="space-y-4">
        {needsRFP.includes("health") && (
          <div id="rfp-form-health" className="scroll-mt-4">
          <HealthRFPForm
            value={
              quoteData.rfpParameters.health || {
                networkTypes: [],
                fundingTypes: [],
                hsaCompatible: false,
                hraCompatible: false,
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  health: value,
                },
              });
            }}
          />
          </div>
        )}

        {needsRFP.includes("dental") && (
          <div id="rfp-form-dental" className="scroll-mt-4">
          <DentalRFPForm
            value={
              quoteData.rfpParameters.dental || {
                coverageTypes: [],
                networkType: "",
                maximumBenefit: 0,
                perioEndoCoverage: "",
                orthodonticCoverage: "",
                orthodonticMaximum: 0,
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  dental: value,
                },
              });
            }}
          />
          </div>
        )}

        {needsRFP.includes("vision") && (
          <div id="rfp-form-vision" className="scroll-mt-4">
          <VisionRFPForm
            value={
              quoteData.rfpParameters.vision || {
                coverageTypes: [],
                materialsCopay: "",
                benefitsFrequency: "",
                allowanceAmount: 0,
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  vision: value,
                },
              });
            }}
          />
          </div>
        )}

        {needsRFP.includes("life") && (
          <div id="rfp-form-life" className="scroll-mt-4">
          <LifeRFPForm
            value={
              quoteData.rfpParameters.life || {
                coverageTypes: [],
                coverageAmounts: [{}],
                voluntaryBuyUp: false,
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  life: value,
                },
              });
            }}
          />
          </div>
        )}

        {needsRFP.includes("disability") && (
          <div id="rfp-form-disability" className="scroll-mt-4">
          <DisabilityRFPForm
            value={
              quoteData.rfpParameters.disability || {
                coverageTypes: [],
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  disability: value,
                },
              });
            }}
          />
          </div>
        )}

        {needsRFP.includes("voluntary") && (
          <div id="rfp-form-voluntary" className="scroll-mt-4">
          <VoluntaryRFPForm
            value={
              quoteData.rfpParameters.voluntary || {
                selectedBenefits: [],
              }
            }
            onChange={(value) => {
              updateQuoteData({
                rfpParameters: {
                  ...quoteData.rfpParameters,
                  voluntary: value,
                },
              });
            }}
          />
          </div>
        )}
      </div>
    </div>
  );
}
